import React from "react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { Link } from "react-router-dom";
import { WeekendWarningDialog } from "@/components/attendance/shared/WeekendWarningDialog";
import { UnifiedLoadingSpinner } from "@/components/loading/UnifiedLoadingSpinner";

import { useSimpleAttendanceState } from "@/hooks/useSimpleAttendanceState";
import { useLateAttendance } from "@/hooks/useLateAttendance";
import { useWeekendCheck } from "@/hooks/useWeekendCheck";
import { memo, useCallback, useMemo, useEffect, useState } from "react";
import { LateStudentsList } from "@/components/attendance/late/LateStudentsList";
import { LateAttendanceControls } from "@/components/attendance/late/LateAttendanceControls";
import { LateAttendanceStats } from "@/components/attendance/late/LateAttendanceStats";

const LateAttendance = memo(() => {
  const { toast } = useToast();

  const [isSaving, setIsSaving] = useState(false);

  const {
    selectedStage,
    selectedGrade,
    selectedSection,
    selectedDate,
    studentsWithAttendance,
    enabledStages,
    hasMultipleStages,
    availableGrades,
    availableSections,
    students,
    loading,
    setSelectedStage,
    setSelectedGrade,
    setSelectedSection,
    setSelectedDate,
    getStageDisplayName,
  } = useSimpleAttendanceState();

  const {
    recordLateAttendance,
    removeLateRecord,
    getStudentLateRecord,
    getLateRecordsForDate,
    lateRecords,
  } = useLateAttendance();

  const {
    showWeekendWarning,
    checkWeekendAndProceed,
    handleWeekendConfirm,
    handleWeekendCancel,
  } = useWeekendCheck();

  // حالة التغييرات المؤقتة للتأخر
  const [lateChanges, setLateChanges] = useState<{
    [studentId: string]:
      | "morning_lineup"
      | "after_lineup"
      | "period_1"
      | "period_2"
      | "period_3"
      | "period_4"
      | "period_5"
      | "period_6"
      | "period_7"
      | null;
  }>({});
  const [lateEditMode, setLateEditMode] = useState(false);

  // إعدادات آمنة لمنع الأخطاء

  useEffect(() => {
    const handleEnableLateEditMode = () => {
      setLateEditMode(true);
      setLateChanges({}); // مسح التغييرات السابقة عند دخول وضع التعديل
    };

    window.addEventListener("enableLateEditMode", handleEnableLateEditMode);
    return () =>
      window.removeEventListener(
        "enableLateEditMode",
        handleEnableLateEditMode
      );
  }, []);

  // console.log("Laterecorders:", lateRecords);
  // سجلات التأخر الحالية مع التحسين
  const currentLateRecords = useMemo(() => {
    if (!selectedDate) return [];
    try {
      if (lateRecords && lateRecords.length > 0) {
        const records = getLateRecordsForDate(selectedDate);

        //         const studentsWithAttendanceData = students.map((student) => {
        //   const attendanceRecord =
        //     records?.find((r) => r?.studentId === student.id) ||
        //     undefined;

        //   return {
        //     ...student,
        //     status: (attendanceRecord?.status === "excused"
        //       ? "absent"
        //       : attendanceRecord?.status) as
        //       | "present"
        //       | "absent"
        //       | "absent_with_reason"
        //       | "late"
        //       | undefined,
        //     isPresent:
        //       attendanceRecord?.status === "present" ||
        //       attendanceRecord?.status === "late",
        //     absenceDays: 0,
        //     lateDays: 0,
        //     attendancePercentage: 0,
        //   };
        // });
        return records;
      } else {
        return [];
      }
    } catch (error) {
      console.error("Error getting late records:", error);
      return [];
    }
  }, [getLateRecordsForDate, selectedDate, lateRecords]);

  const hasExistingLateData = useMemo(
    () => currentLateRecords.length > 0,
    [currentLateRecords.length]
  );

  // تحديث وضع التحرير بناءً على وجود البيانات
  React.useEffect(() => {
    if (!hasExistingLateData) {
      setLateEditMode(true); // تفعيل وضع التحرير تلقائياً إذا لم توجد بيانات
    } else {
      setLateEditMode(false); // إيقاف وضع التحرير إذا وجدت بيانات مسبقة
    }
    setLateChanges({}); // مسح التغييرات عند تغيير البيانات
  }, [hasExistingLateData]);

  // معالج تغيير حالة التأخر (بدون حفظ تلقائي)
  const handleStudentLateStatus = useCallback(
    (
      studentId: string,
      lateType:
        | "morning_lineup"
        | "after_lineup"
        | "period_1"
        | "period_2"
        | "period_3"
        | "period_4"
        | "period_5"
        | "period_6"
        | "period_7"
        | null
    ) => {
      if (!lateEditMode || !studentId) return;

      // حفظ التغيير في الحالة المؤقتة فقط
      setLateChanges((prev) => ({
        ...prev,
        [studentId]: lateType,
      }));
    },
    [lateEditMode]
  );

  // دالة حفظ التأخر مع فحص أيام الإجازة
  const saveLateAttendance = useCallback(async () => {
    const actualSave = async () => {
      if (!selectedGrade || !selectedSection) {
        toast({
          title: "خطأ",
          description: "يرجى اختيار الصف والشعبة",
          variant: "destructive",
        });
        return;
      }

      if (Object.keys(lateChanges).length === 0) {
        toast({
          title: "لا توجد تغييرات",
          description: "لم يتم إجراء أي تغييرات على التأخر",
          variant: "default",
        });
        return;
      }

      setIsSaving(true);

      try {
        const period = undefined;
        const savePromises = [];

        // معالجة كل تغيير في التأخر
        for (const [studentId, lateType] of Object.entries(lateChanges)) {
          if (lateType === null) {
            // إزالة التأخر
            savePromises.push(
              removeLateRecord(studentId, selectedDate, period)
            );
          } else {
            // إضافة أو تحديث التأخر
            savePromises.push(
              recordLateAttendance(studentId, selectedDate, lateType, period)
            );
          }
        }

        await Promise.all(savePromises);

        // مسح التغييرات وإيقاف وضع التعديل
        setLateChanges({});
        setLateEditMode(false);

        const stageText =
          hasMultipleStages && selectedStage
            ? ` - ${getStageDisplayName(selectedStage)}`
            : "";
        const actionText = hasExistingLateData ? "تعديل" : "حفظ";

        toast({
          title: "تم الحفظ بنجاح",
          description: `تم ${actionText} التأخر لـ ${
            Object.keys(lateChanges).length
          } طالب لتاريخ ${selectedDate}${stageText}`,
          variant: "default",
        });
      } catch (error) {
        console.error("Error saving late attendance:", error);
        toast({
          title: "خطأ في الحفظ",
          description: error?.message || "حدث خطأ أثناء حفظ بعض السجلات",
          variant: "destructive",
        });
      } finally {
        setIsSaving(false);
      }
    };

    // التحقق من يوم الإجازة قبل الحفظ
    checkWeekendAndProceed(selectedDate, actualSave);
  }, [
    selectedGrade,
    selectedSection,
    lateChanges,
    recordLateAttendance,
    removeLateRecord,
    selectedDate,
    hasMultipleStages,
    selectedStage,
    getStageDisplayName,
    toast,
    hasExistingLateData,
    checkWeekendAndProceed,
  ]);

  // عرض شاشة التحميل
  if (loading && (!students || students.length === 0)) {
    return (
      <div
        className="container mx-auto px-4 py-2 flex justify-center items-center min-h-[150px]"
        dir="rtl"
      >
        <UnifiedLoadingSpinner size="md" />
      </div>
    );
  }

  return (
    <div
      className="container mx-auto px-4 py-8 pb-32 min-h-screen relative"
      dir="rtl"
    >
      {/* Loading Overlay */}
      {isSaving && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="bg-white rounded-full p-8 shadow-lg">
            <UnifiedLoadingSpinner size="lg" text="جاري تسجيل التأخر..." />
          </div>
        </div>
      )}

      <div className="space-y-6">
        {/* <LazyComponentLoader minHeight="300px"> */}
        <LateAttendanceControls
          title="تسجيل التأخر"
          description="تسجيل تأخر الطلاب اليومي"
          selectedDate={selectedDate}
          selectedStage={selectedStage}
          selectedGrade={selectedGrade}
          selectedSection={selectedSection}
          hasMultipleStages={hasMultipleStages}
          enabledStages={enabledStages}
          availableGrades={availableGrades}
          availableSections={availableSections}
          // loading={loading}
          onDateChange={setSelectedDate}
          onStageChange={setSelectedStage}
          onGradeChange={setSelectedGrade}
          onSectionChange={setSelectedSection}
          getStageDisplayName={getStageDisplayName}
        />
        {/* </LazyComponentLoader> */}

        {/* بطاقات الإحصائيات */}
        {studentsWithAttendance.length > 0 && (
          // <LazyComponentLoader minHeight="80px">
          <LateAttendanceStats
            studentsWithAttendance={studentsWithAttendance}
            currentLateRecords={currentLateRecords}
            lateChanges={lateChanges}
          />
          // </LazyComponentLoader>
        )}

        {studentsWithAttendance.length > 0 && (
          <div className="w-full">
            {/* <LazyComponentLoader minHeight="500px"> */}
            <LateStudentsList
              studentsWithAttendance={studentsWithAttendance}
              selectedGrade={selectedGrade}
              selectedSection={selectedSection}
              selectedStage={selectedStage}
              selectedDate={selectedDate}
              hasMultipleStages={hasMultipleStages}
              hasExistingAttendance={hasExistingLateData}
              isEditMode={lateEditMode}
              studentsLength={students.length}
              loading={loading}
              lateChanges={lateChanges}
              onStudentLateStatusChange={handleStudentLateStatus}
              getStageDisplayName={getStageDisplayName}
              getStudentLateRecord={getStudentLateRecord}
              onSaveAttendance={saveLateAttendance}
              isSaving={isSaving}
            />
            {/* </LazyComponentLoader> */}
          </div>
        )}

        {/* Weekend Warning Dialog */}
        <WeekendWarningDialog
          open={showWeekendWarning}
          onOpenChange={handleWeekendCancel}
          onConfirm={handleWeekendConfirm}
          date={selectedDate}
          type="late"
        />
      </div>
    </div>
  );
});

export default LateAttendance;
